// Detekcija browsera / renderer engine-a uređaja. Šalje se u `version` WS frame-u i pamti se
// uz uređaj na backendu (Devices.Browser / BrowserVersion / EngineVersion / UserAgent), da bi
// se moglo odgovoriti na pitanje "koja je najstarija Chromium baza koja nam je još u opticaju"
// pre podizanja Angular/build target verzije.
//
// ZAŠTO UA string, a ne navigator.userAgentData: userAgentData postoji tek od Chromium-a 90 i
// samo u secure context-u — dakle baš ne postoji na starim klijentima zbog kojih ovo i radimo.
// UA reduction (Chromium 110+) zamrzava minor/build brojeve na 0, ali MAJOR ostaje tačan, a
// major je jedino što nam za ovu odluku treba.
//
// engineVersion je važnije polje od browserVersion: kod Edge/Opera/Samsung/WebView-a brend ima
// svoju numeraciju, a ono što određuje podržani JS/CSS je Chromium baza (token `Chrome/NNN`).

export interface BrowserInfo {
  /** Prikazno ime brenda, npr. "Chrome", "Edge", "Android WebView". Prazno ako nije prepoznat. */
  browser: string;
  /** Major verzija samog brenda (Edg/106, SamsungBrowser/23...). */
  browserVersion: number | null;
  /** Major verzija Chromium baze — null za ne-Chromium browsere (Firefox, Safari). */
  engineVersion: number | null;
  /** Sirov UA string; čuva se kao fallback kad heuristika ispod omane na nepoznatom brendu. */
  userAgent: string;
}

/** Redosled JE bitan: specifičniji brendovi moraju pre Chrome/Safari, jer svi nose i njihov token. */
const BRANDS: { name: string; token: RegExp }[] = [
  { name: 'Edge', token: /\bEdg(?:A|iOS|)\/(\d+)/ },
  { name: 'Opera', token: /\bOPR\/(\d+)/ },
  { name: 'Samsung Internet', token: /\bSamsungBrowser\/(\d+)/ },
  { name: 'Yandex', token: /\bYaBrowser\/(\d+)/ },
  { name: 'Vivaldi', token: /\bVivaldi\/(\d+)/ },
  { name: 'Electron', token: /\bElectron\/(\d+)/ },
  { name: 'Firefox', token: /\b(?:Firefox|FxiOS)\/(\d+)/ },
  { name: 'Chromium', token: /\bChromium\/(\d+)/ },
  { name: 'Chrome', token: /\bChrome\/(\d+)/ },
];

function majorFrom(ua: string, pattern: RegExp): number | null {
  const match = pattern.exec(ua);
  if (!match) {
    return null;
  }

  const major = parseInt(match[1], 10);
  return isNaN(major) ? null : major;
}

export function parseBrowserInfo(ua: string): BrowserInfo {
  const result: BrowserInfo = {
    browser: '',
    browserVersion: null,
    engineVersion: null,
    userAgent: ua ?? '',
  };

  if (!ua) {
    return result;
  }

  // Chromium baza — `Chromium/N` (Linux paketi, CEF) ili `Chrome/N` (sve ostalo, uključujući
  // Edge, Operu, Samsung i Android WebView).
  result.engineVersion = majorFrom(ua, /\bChromium\/(\d+)/) ?? majorFrom(ua, /\bChrome\/(\d+)/);

  for (const brand of BRANDS) {
    const major = majorFrom(ua, brand.token);
    if (major != null) {
      result.browser = brand.name;
      result.browserVersion = major;
      break;
    }
  }

  // Android WebView se od Chrome-a razlikuje samo po `wv` markeru — brend prepravljamo tek ovde
  // da bi engineVersion (Chromium baza WebView-a) ostao pročitan iz istog `Chrome/N` tokena.
  if (result.browser === 'Chrome' && /;\s*wv[;)]/.test(ua)) {
    result.browser = 'Android WebView';
  }

  // Safari nema svoj brend token — prepoznaje se po `Safari/` BEZ ijednog Chromium tokena.
  // Verzija stoji u `Version/N`. iOS WebView (WKWebView) nema ni Version/ ni Safari/ marker,
  // pa ostaje neprepoznat i oslanjamo se na sirov UA.
  if (!result.browser && /\bSafari\//.test(ua) && result.engineVersion == null) {
    result.browser = 'Safari';
    result.browserVersion = majorFrom(ua, /\bVersion\/(\d+)/);
  }

  return result;
}

export function getBrowserInfo(): BrowserInfo {
  try {
    return parseBrowserInfo(typeof navigator === 'undefined' ? '' : navigator.userAgent);
  } catch {
    return { browser: '', browserVersion: null, engineVersion: null, userAgent: '' };
  }
}

/**
 * Prikazni zapis browsera za admin ekrane: "Chrome 128", odnosno "Edge 127 (Chromium 128)" kad
 * brend ima svoju numeraciju različitu od Chromium baze. Prazno kad uređaj još nije prijavio
 * ništa upotrebljivo — pozivalac tada može da padne na sirov userAgent.
 */
export function formatBrowserLabel(info: {
  browser?: string | null;
  browserVersion?: number | null;
  engineVersion?: number | null;
}): string {
  if (!info?.browser) {
    return '';
  }

  const label = info.browserVersion ? `${info.browser} ${info.browserVersion}` : info.browser;

  if (info.engineVersion != null && info.engineVersion !== info.browserVersion) {
    return `${label} (Chromium ${info.engineVersion})`;
  }

  return label;
}